import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  IconButton,
  Drawer,
  Button,
  Divider,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import GroupSelector from './GroupSelector';
import MembersList from './MembersList';
import PeriodSelector from './PeriodSelector';
import MenuIcon from '@mui/icons-material/Menu';
import GroupIcon from '@mui/icons-material/Group';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PeopleIcon from '@mui/icons-material/People';
import CloseIcon from '@mui/icons-material/Close';

export default function EnhancedNavbar({
  userInfo,
  groups,
  currentGroup,
  refreshGroups,
  onGroupChange,
  validPeriods,
  selectedPeriod,
  onPeriodChange,
  groupMembers,
  onMemberSelect,
  selectedMember,
  isCurrentPeriod,
  renderMembersListInNavbar = false
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [periodDialogOpen, setPeriodDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);

  const pagesList = [
    { 
      pageName: "Home",
      url: "/"
    },
    { 
      pageName: "Settings",
      url: "/settings"
    }
  ];

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/signin");
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  // Render the group selector in a dialog for mobile
  const renderGroupDialog = () => (
    <Dialog 
      open={groupDialogOpen} 
      onClose={() => setGroupDialogOpen(false)}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}> Select Group </Typography>
          <IconButton onClick={() => setGroupDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <GroupSelector
          groups={groups}
          currentGroup={currentGroup}
          refreshGroups={refreshGroups}
          onGroupChange={(groupId) => {
            onGroupChange(groupId);
            setGroupDialogOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );

  // Render the period selector in a dialog for mobile
  const renderPeriodDialog = () => (
    <Dialog 
      open={periodDialogOpen} 
      onClose={() => setPeriodDialogOpen(false)}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}> Select a Period </Typography>
          <IconButton onClick={() => setPeriodDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <PeriodSelector
          validPeriods={validPeriods}
          selectedPeriod={selectedPeriod}
          onPeriodChange={(period) => {
            onPeriodChange(period);
            setPeriodDialogOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );

  // Render the members list in a dialog for mobile
  const renderMembersDialog = () => (
    <Dialog 
      open={membersDialogOpen} 
      onClose={() => setMembersDialogOpen(false)}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Select Member</Typography>
          <IconButton onClick={() => setMembersDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <MembersList
          groupMembers={groupMembers}
          onMemberSelect={(member) => {
            onMemberSelect(member);
            setMembersDialogOpen(false);
          }}
          selectedMember={selectedMember}
          isCurrentPeriod={isCurrentPeriod}
          currentGroup={currentGroup}
          refreshGroups={refreshGroups}
          compact={true}
        />
      </DialogContent>
    </Dialog>
  );

  // Get the current group name
  // Check if groups is an array and not empty before trying to find
  const currentGroupName = Array.isArray(groups) && groups.length > 0 
    ? (groups.find(g => g.id === currentGroup)?.groupName || 'Select Group')
    : 'Select Group';
  // Get the current period formatted
  let currentPeriodFormatted = validPeriods.find(p => p.weekStart === selectedPeriod)?.weekStart || 'Select Period';
  if (currentPeriodFormatted !== 'Select Period') {
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    };
    currentPeriodFormatted = formatDate(currentPeriodFormatted);
  }
  
  // Get the current member name
  const currentMemberName = selectedMember?.name;

  // Only show the members button if we're showing the members list in navbar (mobile only)
  const shouldShowMembersButton = renderMembersListInNavbar;

  // Create button text with appropriate length limits
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const groupButtonText = truncateText(currentGroupName, 20);
  const periodButtonText = truncateText(currentPeriodFormatted, 20);
  const memberButtonText = truncateText(currentMemberName, 20);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: "100vw",
          left: 0,
          top: 0,
          height: { xs: '64px', sm: '70px' },
          zIndex: 1100
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleDrawer(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1 }}>
            {pagesList.map((page) => (
              <MenuItem key={page.pageName} onClick={() => navigate(page.url)} sx={{ borderRadius: "15px" }}>
                <Typography sx={{ textAlign: 'center' }}>{page.pageName}</Typography>
              </MenuItem>
            ))}
          </Box>

          {/* Desktop selectors */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <Tooltip title={currentGroupName.length > 20 ? currentGroupName : ""}>
                <Button 
                  variant="outlined" 
                  color="inherit" 
                  startIcon={<GroupIcon />}
                  onClick={() => setGroupDialogOpen(true)}
                  sx={{ mr: 1, textTransform: 'none' }}
                >
                  {groupButtonText}
                </Button>
              </Tooltip>
              
              <Tooltip title={currentPeriodFormatted.length > 20 ? currentPeriodFormatted : ""}>
                <Button 
                  variant="outlined" 
                  color="inherit" 
                  startIcon={<DateRangeIcon />}
                  onClick={() => setPeriodDialogOpen(true)}
                  sx={{ mr: 1, textTransform: 'none' }}
                >
                  {periodButtonText}
                </Button>
              </Tooltip>
              
              {shouldShowMembersButton && (
                <Tooltip title={currentMemberName.length > 20 ? currentMemberName : ""}>
                  <Button 
                    variant="outlined" 
                    color="inherit" 
                    startIcon={<PeopleIcon />}
                    onClick={() => setMembersDialogOpen(true)}
                    sx={{ mr: 1, textTransform: 'none' }}
                  >
                    {memberButtonText}
                  </Button>
                </Tooltip>
              )}
            </Box>
          )}

          <Box sx={{ flexGrow: isMobile ? 1 : 0, display: 'flex', justifyContent: 'flex-end' }}>
            <Typography sx={{ alignSelf: 'center', mr: 1 }}>{userInfo?.name}</Typography>
            <IconButton size="large" onClick={handleMenu} color="inherit">
              <Avatar src={userInfo?.profilePhotoUrl} alt={userInfo?.name} />
            </IconButton>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">Bondly</Typography>
          </Box>
          <Divider />
          
          {pagesList.map((page) => (
            <MenuItem 
              key={page.pageName} 
              onClick={() => {
                navigate(page.url);
                setDrawerOpen(false);
              }}
            >
              <Typography>{page.pageName}</Typography>
            </MenuItem>
          ))}
          
          <Divider />
          
          <MenuItem onClick={() => {
            setGroupDialogOpen(true);
            setDrawerOpen(false);
          }}>
            <GroupIcon sx={{ mr: 1 }} />
            <Typography noWrap>Group: {currentGroupName}</Typography>
          </MenuItem>
          
          <MenuItem onClick={() => {
            setPeriodDialogOpen(true);
            setDrawerOpen(false);
          }}>
            <DateRangeIcon sx={{ mr: 1 }} />
            <Typography noWrap>Period: {currentPeriodFormatted}</Typography>
          </MenuItem>
          
          {shouldShowMembersButton && (
            <MenuItem onClick={() => {
              setMembersDialogOpen(true);
              setDrawerOpen(false);
            }}>
              <PeopleIcon sx={{ mr: 1 }} />
              <Typography noWrap>
                {currentMemberName ? `Member: ${currentMemberName}` : 'Members List'}
              </Typography>
            </MenuItem>
          )}
        </Box>
      </Drawer>

      {/* Render dialogs */}
      {renderGroupDialog()}
      {renderPeriodDialog()}
      {renderMembersDialog()}
    </>
  );
} 